/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// material
import { Switch, TextField, InlineMessage, sendErrorMessage } from '@insticator/insticator-ui'; // iui-material
import AdvanceSettings from 'pages/dashboard/commenting/configuration/generalsection/advancesettings/advancesettings';
import FontSettings from 'pages/dashboard/commenting/configuration/generalsection/fontsettings/fontsettings';
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionDescription from 'material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription'
import ConfigController from 'material/expandablesection/controllers/configcontroller/configcontroller'
// actions
import { updateGeneralSettings, updateGeneral, updateGeneralText, validateGeneralText, cancelSectionChanges } from 'state/actions/commentingState/configurationStatus';
// utils
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/commenting/configuration/generalsection/generalsection_styles';


const GeneralSection = (props) => {

    // action dispatch
    const dispatch = useDispatch();
    const updateGeneralState = (prop, value) => dispatch(updateGeneralSettings(prop, value));
    const updateGeneralTextState = (prop, value) => dispatch(updateGeneralText(prop, value));
    const validateGeneralTextState = (prop, valid) => dispatch(validateGeneralText(prop, valid));
    const restoreStateOnCancel = (section) => dispatch(cancelSectionChanges(section));
    const saveGeneralSettings = (uuid, data, callBack) => dispatch(updateGeneral(uuid, data, callBack));

    // redux state
    const mocked = useSelector(({ sessionStatus }) => sessionStatus.mocked);
    const updated = useSelector(({ commentingState }) => commentingState.configStatus.general.updated);
    const savingGeneral = useSelector(({ commentingState }) => commentingState.configStatus.general.savingGeneral);
    const uploadingLogo = useSelector(({ commentingState }) => commentingState.configStatus.general.uploadingLogo);
    const commentingUnitConfigurationUUID = useSelector(({ commentingState }) => commentingState.configStatus.commentingUnitConfigurationUUID);
    const name = useSelector(({ commentingState }) => commentingState.configStatus.general.name);
    const isPageIDEnabled = useSelector(({ commentingState }) => commentingState.configStatus.general.isPageIDEnabled);
    const isPromoteCommentsEnabled = useSelector(({ commentingState }) => commentingState.configStatus.general.isPromoteCommentsEnabled);
    const isTurnOffRepliesEnabled = useSelector(({ commentingState }) => commentingState.configStatus.general.isTurnOffRepliesEnabled);
    const isActiveUsersIndicatorEnabled = useSelector(({ commentingState }) => commentingState.configStatus.general.isActiveUsersIndicatorEnabled);
    const activeUsersIndicatorTitle = useSelector(({ commentingState }) => commentingState.configStatus.general.activeUsersIndicatorTitle);
    const fontSize = useSelector(({ commentingState }) => commentingState.configStatus.general.fontSize);
    const fontWeight = useSelector(( {commentingState }) => commentingState.configStatus.general.weight);
    const group = useSelector(({ commentingState }) => commentingState.configStatus.general.advancedSettings.group);
    const isLoadCommentsButtonEnabled = useSelector(({ commentingState }) => commentingState.configStatus.general.advancedSettings.isLoadCommentsButtonEnabled);
    const allowDisqusAccountLink = useSelector(({ commentingState }) => commentingState.configStatus.general.advancedSettings.allowDisqusAccountLink);
    
    // component state
    const [isOpened, setIsOpened] = useState(false);

    const nameTest = /^(?!\s)[!@#$%^&*(){}[\]<>?/|.:;_A-Za-z0-9'\-\s]{1,191}$/;
    const userIndicatorTest = /^(?!\s)[!@#$%^&*(){}[\]<>?/|.:;_A-Za-z0-9'\-\s]{0,64}$/;

    const validateName = e => validateGeneralTextState('name', nameTest.test(e.target.value));
    const validateIndicator = e => validateGeneralTextState('activeUsersIndicatorTitle', userIndicatorTest.test(e.target.value));

    const saveData = () => {
        const advancedSettings = ({
            group,
            isLoadCommentsButtonEnabled,
            allowDisqusAccountLink,
        })

        return ({
            name: name.value,
            isPromoteCommentsEnabled,
            isPageIDEnabled,
            isTurnOffRepliesEnabled,
            isActiveUsersIndicatorEnabled,
            activeUsersIndicatorTitle: activeUsersIndicatorTitle.value,
            fontSize,
            weight: fontWeight,
            advancedSettings: mocked ? advancedSettings : null
        })
    }

    const isSectionValid = () => name.valid && activeUsersIndicatorTitle.valid

    const saveGeneral = () => {
        if(!isSectionValid()){
            return dispatch(sendErrorMessage('Cannot save: invalid fields'));
        }
        saveGeneralSettings(commentingUnitConfigurationUUID, saveData(), () => setIsOpened(false))
    }

    const controller =  (
        <ConfigController
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            disableSave={!updated || uploadingLogo}
            processing={savingGeneral}
            handleCancel={() => { restoreStateOnCancel('general') }}
            handleSave={saveGeneral}
            title='General'
            description={<SectionDescription
                main='Promoted comments'
                additional='Users can turn off replies, +3 more'
            />}
        />
    );

    const pageIDCopy = <span>Allows to ensure that your comments surface in a persistent manner on a given article under different URLs (e.g. when the URL of an article changes automatically over time).</span>;


    return (
        <ExpandableSection
            className={`generalsection ${props.classes.generalsection}`}
            controller={controller}
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            disableSave={!updated || uploadingLogo}
            processing={savingGeneral}
            handleCancel={() => restoreStateOnCancel('general')}
            handleSave={saveGeneral}
        >
            <SectionCard>
                <SectionGroup>
                    <SectionGroupDescription
                        title={'Integration name'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <TextField
                        type={'text'}
                        required={true}
                        name={'integrationName'}
                        value={name.value}
                        valid={name.valid}
                        handleBlur={validateName}
                        handleChange={e => updateGeneralTextState('name', e.target.value)}
                        placeholder={'website.com'}
                    />
                    <SectionGroup row>
                        <SectionGroupDescription
                            title={'Enable External Page ID Functionality'}
                            description={pageIDCopy}
                        />
                        <Switch
                            isStatic={true}
                            handleClick={() => updateGeneralState('isPageIDEnabled', !isPageIDEnabled)}
                            on={isPageIDEnabled}
                            variant='secondary'
                        />
                    </SectionGroup>
                    <InlineMessage
                        key={'meow'}
                        display={true}
                        name={'headercode'}
                        variant={'neutral'}
                        icon={'exclamation-triangle'}
                        title={null}
                        message={<span>Enabling or disabling External Page ID affects the embed codes for the Commenting Unit and for the Comments Counter.</span>}
                        close={false}
                        handleClose={null} />
                </SectionGroup>
            </SectionCard>
            <SectionCard>
                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Promoted Comments'}
                        description={'Enable pinning of a comment to the top of the section.'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateGeneralState('isPromoteCommentsEnabled', !isPromoteCommentsEnabled)}
                        on={isPromoteCommentsEnabled}
                        variant='secondary'
                    />
                </SectionGroup>

                {/*{generalStatus.isPromoteCommentsEnabled &&*/}
                {/*<SectionGroup secondary>*/}
                {/*  <SectionGroupDescription*/}
                {/*    title={'Access to promote'}*/}
                {/*    description={'Allow users to spend points to pin their comment to the top of a commenting section, or let your moderators decide.'}*/}
                {/*  />*/}
                {/*  <Group name={'controls'} >*/}
                {/*    <Control text={'Users'} selected={generalStatus.promotedBy === 'USERS'} value={'USERS'} variant={'secondary'} addCheckWhenSelected={true}/>*/}
                {/*    <Control text={'Moderators'} selected={generalStatus.promotedBy === 'MODERATORS'} value={'MODERATORS'} variant={'secondary'} addCheckWhenSelected={true}/>*/}
                {/*  </Group>*/}
                {/*</SectionGroup>}*/}

                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Allow users to turn off replies'}
                        description={'Users can choose to turn off replies to their comments.'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateGeneralState('isTurnOffRepliesEnabled', !isTurnOffRepliesEnabled)}
                        on={isTurnOffRepliesEnabled}
                        variant={'secondary'}
                    />
                </SectionGroup>

                <SectionGroup row>
                    <SectionGroupDescription
                        title={'«Active on page» indicator'}
                        description={'Shows what users are currently active on the page.'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateGeneralState('isActiveUsersIndicatorEnabled', !isActiveUsersIndicatorEnabled)}
                        on={isActiveUsersIndicatorEnabled}
                        variant={'secondary'}
                    />
                </SectionGroup>

                {isActiveUsersIndicatorEnabled && <SectionGroup secondary>
                    <SectionGroupDescription
                        title={'Indicator title'}
                    />
                    <TextField
                        type={'text'}
                        name={'activeUsersIndicatorTitle'}
                        required={true}
                        handleChange={e => updateGeneralTextState('activeUsersIndicatorTitle', e.target.value)}
                        handleBlur={validateIndicator}
                        value={activeUsersIndicatorTitle.value}
                        valid={activeUsersIndicatorTitle.valid}
                        placeholder={'Active on Page'}
                    />
                </SectionGroup>}
            </SectionCard>

            <FontSettings/>

            {mocked && <AdvanceSettings/>}
        </ExpandableSection>)
};

export default injectSheet(styles)(GeneralSection)
